<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, nextTick } from "vue";
import { trans } from "laravel-vue-i18n";
import { MembershipData } from "@/ts/components/members";
import { format } from "fecha";
import { Checkbox, Submit } from "@/ts/components/form";
import { useForm } from "vee-validate";
import * as Yup from "yup";
import { nl } from "yup-locales";

interface Props {
    data: MembershipData;
}

const component = ref<HTMLDivElement>();
const props = defineProps<Props>();

const emit = defineEmits(["submit", "back", "mounted"]);

const schema = computed(() =>
    Yup.object({
        privacy: Yup.bool().oneOf([true]),
    }),
);

const { handleSubmit, meta } = useForm({
    initialValues: { privacy: false },
    validationSchema: schema,
});

onMounted(async () => {
    await nextTick();

    const updateHeight = () => {
        if (component.value) {
            const height = component.value.getBoundingClientRect().height;
            emit("mounted", height);
        }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
        updateHeight();
    });

    if (component.value) {
        resizeObserver.observe(component.value);
    }

    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);

    // Clean up
    onUnmounted(() => {
        if (component.value) {
            resizeObserver.unobserve(component.value);
        }
        window.removeEventListener("resize", updateHeight);
        window.removeEventListener("orientationchange", updateHeight);
    });

    // scroll to top
    window.scrollTo(0, 0);
});

const durationString = computed(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    const endDate = new Date(year + props.data.period!, month, day);
    return `${format(today, "D MMM YYYY")} tot ${format(endDate, "D MMM YYYY")}`;
});
</script>

<template>
    <div ref="component" class="w-full px-4 pt-10 pb-4 md:p-10 lg:p-[3.75rem]">
        <button class="group mb-4 inline-flex items-center text-14 uppercase !leading-normal" @click="emit('back')">
            <span class="mr-2 rotate-180 transform transition-transform group-hover:-translate-x-1">
                <svg-vue icon="arrow" class="h-4 w-auto"></svg-vue>
            </span>
            Terug
        </button>
        <h2 class="mb-6 text-center font-heading text-40 font-[690] uppercase [font-stretch:_190%] md:mb-4 lg:text-48">{{ trans("membership.summary_title") }}</h2>
        <div class="mb-10 flex flex-col lg:flex-row lg:items-center lg:justify-between bg-blue p-6 lg:p-10">
            <div class="flex flex-col mb-4 lg:mb-0">
                <h2 class="lg:text-center font-heading text-32 font-[690] uppercase [font-stretch:_190%]">{{ data.period }} {{ trans("membership.years_membership") }}</h2>
                <p class="text-[.75rem] font-medium leading-4">{{ durationString }}</p>
            </div>
            <div class="flex flex-col lg:items-end lg:justify-end">
                <span class="mb-[.125rem]">&euro; 000</span>
                <span class="text-[.75rem] font-medium leading-4">incl.btw</span>
            </div>
        </div>
        <div class="-m-5 md:-m-3 flex flex-wrap">
            <div class="w-full p-5 md:p-3 md:w-1/2">
                <p class="mb-4 font-bold">{{ trans("membership.contact_info") }}</p>
                <div class="flex flex-col">
                    <span class="mb-2">{{ data.personal_info.first_name }} {{ data.personal_info.last_name }}</span>
                    <span class="mb-2">{{ data.personal_info.email }}</span>
                    <span class="mb-2">{{ data.personal_info.phone }}</span>
                    <span>{{ data.personal_info.birth_date }}</span>
                </div>
            </div>
            <div class="w-full p-5 md:p-3 md:w-1/2">
                <p class="mb-4 font-bold">{{ trans("membership.address_info") }}</p>
                <div class="flex flex-col">
                    <span class="mb-2">{{ data.personal_info.street }} {{ data.personal_info.number }} {{ data.personal_info.addition }}</span>
                    <span>{{ data.personal_info.postal_code }} {{ data.personal_info.city }}</span>
                </div>
            </div>
        </div>
        <form @submit="onSubmit" class="mt-10 flex flex-col lg:flex-row items-center justify-between gap-10 xl:gap-12">
            <checkbox name="privacy">
                <span v-html="trans('membership.privacy_policy')"></span>
            </checkbox>
            <submit class="btn btn-black w-full md:w-auto" :disabled="!meta.valid">
                {{ trans("membership.checkout") }}
                <svg-vue icon="arrow" class="ml-[.625rem] h-6 w-auto"></svg-vue>
            </submit>
        </form>
    </div>
</template>
